import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import type { ContactPlatform } from '@/config/contactDetails';

/**
 * Lets the page tell the HEADER which workflow is open.
 *
 * The header lives in `AppShell`, above the router outlet, so it cannot see the
 * template the page just fetched — and the contact number it has to show
 * depends on that template's platform (IFDS for the registered plans, APEX for
 * the subscription agreements). This is the smallest thing that closes that
 * gap: one value, set by the page that already has it, read by the header.
 *
 * Deliberately not a store or a query cache. It is a single presentational fact
 * with exactly one writer and one reader, and it resets to null on unmount so a
 * stale platform cannot outlive the page that set it — which is what would send
 * an advisor the wrong support number after navigating away.
 */
type SmartFormContextValue = {
  platform: ContactPlatform | null;
  setPlatform: (platform: ContactPlatform | null) => void;
};

const SmartFormContext = createContext<SmartFormContextValue>({
  platform: null,
  setPlatform: () => {},
});

export function SmartFormProvider({ children }: { children: ReactNode }) {
  const [platform, setPlatform] = useState<ContactPlatform | null>(null);
  const value = useMemo(() => ({ platform, setPlatform }), [platform]);
  return <SmartFormContext.Provider value={value}>{children}</SmartFormContext.Provider>;
}

/** Read the active workflow's platform — the header's only consumer. */
export function useSmartFormPlatform(): ContactPlatform | null {
  return useContext(SmartFormContext).platform;
}

/** Publish the active workflow's platform. Call from the page that loads the
 *  template; pass null on unmount so it does not outlive that page. */
export function useSetSmartFormPlatform(): (platform: ContactPlatform | null) => void {
  return useContext(SmartFormContext).setPlatform;
}
